import { BaseApiService } from '@/shared/api/services/base.service';

export interface AlbumGenerateRequest {
  // Design axis — always 'standard' for now (the only selectable template).
  template?: 'standard' | 'architect' | 'studio';
  // Content-section toggles — all default ON client-side and are always sent.
  // `include_blueprints` gates ONLY the facade projections (blueprint
  // drawings); the other sections each carry their own flag.
  include_blueprints?: boolean;
  include_materials?: boolean;
  include_preparation_guide?: boolean;
  include_safety_requirements?: boolean;
  include_building_regulations?: boolean;
  language?: string;
  // Measurement system for the album's dimensions and quantities. Omitted
  // entirely when the user keeps "Auto" — the server then decides. Mirrors
  // Apple (`nullable|in:metric,imperial` server-side).
  measurement_system?: 'metric' | 'imperial';
  selected_renders: string[];
  requirements?: string;
  force_regenerate?: boolean;
  // Extended thinking — when true, the album-content LLM uses a stronger
  // reasoning model. Pro+ gated server-side. Default false.
  is_extended_thinking?: boolean;
  // Optional cost estimate to embed in the album. When set, the album uses the
  // referenced (possibly user-edited) estimate as-is; it may belong to any
  // design of the same building.
  estimate_id?: string | null;
  // "New estimate" picker option: the server generates a fresh estimate for
  // this design inside the album pipeline and embeds it. Mutually exclusive
  // with estimate_id. When NEITHER field is sent the server falls back to the
  // concept's latest ready estimate (or none → cost sections omitted).
  generate_new_estimate?: boolean;
  // "Don't include" picker option — the album skips the cost sections
  // entirely. Also sent (false) while the plan lacks the cost_estimate perk.
  include_estimate?: boolean;
}

/**
 * Query params of `GET /album/cost-prediction` — the settings that influence
 * the predicted token cost of an album generation. Mirrors the generate
 * request's cost-relevant subset; booleans are serialized as `true`/`false`.
 */
export interface AlbumCostPredictionParams {
  renders_count: number;
  include_materials: boolean;
  include_blueprints: boolean;
  include_preparation_guide: boolean;
  include_safety_requirements: boolean;
  include_building_regulations: boolean;
  is_extended_thinking: boolean;
  generate_new_estimate: boolean;
}

/**
 * Attributes of `GET /album/cost-prediction`. `is_significant_cost` is a
 * server-computed flag (threshold lives in server config) and
 * `warning_message` is server-localized (Accept-Language), `null` when the
 * cost is not significant. Clients MUST NOT compare `tokens` to any threshold
 * or compose warning text — read the flag + message only (API-driven state
 * flags / API-driven messages rules).
 */
export interface AlbumCostPrediction {
  tokens: number;
  is_significant_cost: boolean;
  warning_message: string | null;
}

export interface AlbumStatus {
  id: string;
  concept_id?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  download_url?: string;
  filename?: string;
  file_size?: string;
  generated_at?: string;
  error?: string;
  error_message?: string;
  message?: string;
  duplicate_exists?: boolean;
  album_title?: string;
  public_url?: string;
  page_count?: number;
}

export interface Album {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  template: string;
  // Whether blueprints were bundled into this album (server-driven). Drives the
  // "blueprint" vs "renders only" display label, replacing the old template axis.
  include_blueprints: boolean;
  format: string;
  language: string;
  whitelabel: boolean;
  created_at: string;
  // Timing for the active album — surfaces on /album for all statuses since
  // API commit 553bcd708 ("Atomic album lifecycle and scaled duration
  // prediction"). Used by the L4 watchdog's `reconcileAlbumsFromApi` to
  // patch a stale pending job into processing without losing the timer.
  started_at?: string | null;
  duration_predicted?: number | null;
  album_title?: string;
  public_url?: string;
  filename?: string;
  file_size?: string;
  page_count?: number;
  generated_at?: string;
  expires_at?: string;
}

export interface ProjectAlbumsEntry {
  concept: {
    id: string;
    title: string | null;
    project_sequence_number: number;
    main_render_url: string | null;
  };
  albums: Album[];
}

class AlbumService extends BaseApiService {
  async generate(
    conceptId: string,
    data: AlbumGenerateRequest
  ): Promise<AlbumStatus> {
    const response = await this.post<{
      data: { id: string; attributes: Omit<AlbumStatus, 'id'> };
    }>(
      `/v1/concepts/${conceptId}/album/generate`,
      this.formatJsonApiData('album', data as unknown as Record<string, unknown>)
    );
    return {
      id: response.data.id,
      ...response.data.attributes,
    };
  }

  /**
   * Predicted token cost of an album generation for the given settings —
   * server-computed (NO client-side price math). The significant-cost flag
   * and localized warning come from the server too; the client only renders
   * them (Generate-button suffix + warning NoticeBanner).
   */
  async costPrediction(
    conceptId: string,
    params: AlbumCostPredictionParams
  ): Promise<AlbumCostPrediction> {
    const query = this.buildQueryString({ ...params });
    const response = await this.httpGet<{
      data: { id: string; attributes: AlbumCostPrediction };
    }>(`/v1/concepts/${conceptId}/album/cost-prediction?${query}`);
    return response.data.attributes;
  }

  async list(conceptId: string): Promise<Album[]> {
    const response = await this.httpGet<{
      data: Array<{ id: string; attributes: Omit<Album, 'id'> }>;
    }>(`/v1/concepts/${conceptId}/album`);
    return response.data.map((item) => ({
      id: item.id,
      ...item.attributes,
    }));
  }

  async listByProject(projectId: string): Promise<ProjectAlbumsEntry[]> {
    const response = await this.httpGet<{
      data: Array<{
        concept: {
          type: string;
          id: string;
          attributes: {
            title: string | null;
            project_sequence_number: number;
            main_render_url: string | null;
          };
        };
        albums: Array<{
          type: string;
          id: string;
          attributes: Omit<Album, 'id'>;
        }>;
      }>;
    }>(`/v1/projects/${projectId}/albums`);
    return response.data.map((entry) => ({
      concept: {
        id: entry.concept.id,
        title: entry.concept.attributes.title,
        project_sequence_number:
          entry.concept.attributes.project_sequence_number,
        main_render_url: entry.concept.attributes.main_render_url,
      },
      albums: entry.albums.map((item) => ({
        id: item.id,
        ...item.attributes,
      })),
    }));
  }

  async delete(conceptId: string, albumId: string): Promise<void> {
    await this.httpDelete(`/v1/concepts/${conceptId}/album/${albumId}`);
  }

  async restore(conceptId: string, albumId: string): Promise<void> {
    await this.post(`/v1/concepts/${conceptId}/album/${albumId}/restore`);
  }
}

export const albumApi = new AlbumService();
